import { BloomMode } from "./scene";
import {
  ADAPTIVE_TARGET_FPS_CAP,
  ADAPTIVE_TARGET_FPS_FLOOR,
  ADAPTIVE_REFRESH_RATES,
  ADAPTIVE_WARMUP_MS,
  ADAPTIVE_FPS_RATIO,
  ADAPTIVE_EVAL_MS,
  ADAPTIVE_SETTLE_MS,
} from "./constants";

interface QualityStep {
  bloom: BloomMode;
  dprCap1: boolean;
}

export interface AdaptiveQualityHooks {
  setBloomMode: (mode: BloomMode) => void;
  setDprCap1: (cap: boolean) => void;
  onStep: (bloom: BloomMode, dprCap1: boolean) => void;
}

const LADDER: QualityStep[] = [
  { bloom: "half", dprCap1: false },
  { bloom: "quarter", dprCap1: false },
  { bloom: "quarter", dprCap1: true },
];

function median(values: number[]): number {
  const sorted = [...values].sort((a: number, b: number): number => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function snapRefreshRate(fps: number): number {
  let best = ADAPTIVE_REFRESH_RATES[0];
  let bestDiff = Infinity;
  for (const rate of ADAPTIVE_REFRESH_RATES) {
    const diff = Math.abs(rate - fps);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = rate;
    }
  }
  return best;
}

function nextLowerTarget(current: number): number {
  let best = ADAPTIVE_TARGET_FPS_FLOOR;
  for (const rate of ADAPTIVE_REFRESH_RATES) {
    if (rate >= ADAPTIVE_TARGET_FPS_FLOOR && rate < current && rate > best) {
      best = rate;
    }
  }
  return best;
}

/**
 * One-way adaptive quality governor with two phases. After a warmup window
 * measures the display refresh rate from instantaneous frame deltas (snapped to
 * a common rate, capped at ADAPTIVE_TARGET_FPS_CAP), it reacts whenever the
 * smoothed FPS stays below target * ratio for ADAPTIVE_EVAL_MS.
 *
 * Quality degradation runs first: each trigger steps down the LADDER, changing
 * exactly one knob (bloom resolution, then DPR cap) so only that knob's hook
 * fires, lowering the look to hold the measured target FPS. Target relaxation is
 * the last resort: only once the LADDER is exhausted and FPS still falls short
 * does the target drop one refresh-rate tier toward ADAPTIVE_TARGET_FPS_FLOOR
 * (30). This keeps a capable-after-degradation device at its full refresh rate
 * (e.g. bloom=quarter + DPR=1 to hold 60) rather than settling at a lower frame
 * rate with untouched quality.
 *
 * A settle period after each step prevents the pre-step low FPS from cascading
 * further before the change takes effect. It never recovers — once lowered,
 * target and quality stay lowered for the session, avoiding oscillation at the
 * boundary between quality cost and audio load.
 *
 * The caller is responsible for not constructing this when a ?bloom= / ?dpr1= /
 * ?bench= query param pins quality (those paths require fixed, deterministic
 * quality and must not be touched here).
 */
export class AdaptiveQuality {
  private target: number = ADAPTIVE_TARGET_FPS_CAP;
  private readonly samples: number[] = [];
  private warmedUp: boolean = false;
  private warmupStart: number = -1;
  private level: number = 0;
  private belowSince: number = -1;
  private settleUntil: number = 0;

  constructor(private readonly hooks: AdaptiveQualityHooks) {}

  get currentTarget(): number {
    return this.target;
  }

  update(now: number, instMs: number, emaFps: number): void {
    if (!this.warmedUp) {
      this.warmup(now, instMs);
      return;
    }
    if (this.level >= LADDER.length - 1 && this.target <= ADAPTIVE_TARGET_FPS_FLOOR) {
      return;
    }
    if (now < this.settleUntil) {
      this.belowSince = -1;
      return;
    }
    if (emaFps < this.target * ADAPTIVE_FPS_RATIO) {
      if (this.belowSince < 0) {
        this.belowSince = now;
      } else if (now - this.belowSince >= ADAPTIVE_EVAL_MS) {
        if (this.level < LADDER.length - 1) {
          this.stepDownQuality();
        } else {
          this.relaxTarget();
        }
        this.settleUntil = now + ADAPTIVE_SETTLE_MS;
        this.belowSince = -1;
      }
    } else {
      this.belowSince = -1;
    }
  }

  private warmup(now: number, instMs: number): void {
    if (this.warmupStart < 0) {
      this.warmupStart = now;
    }
    if (instMs >= 1 && instMs <= 100) {
      this.samples.push(instMs);
    }
    if (now - this.warmupStart < ADAPTIVE_WARMUP_MS) {
      return;
    }
    if (this.samples.length > 0) {
      const measured = 1000 / median(this.samples);
      this.target = Math.min(snapRefreshRate(measured), ADAPTIVE_TARGET_FPS_CAP);
    }
    this.warmedUp = true;
  }

  private relaxTarget(): void {
    this.target = nextLowerTarget(this.target);
  }

  private stepDownQuality(): void {
    const prev = LADDER[this.level];
    this.level += 1;
    const cur = LADDER[this.level];
    if (cur.bloom !== prev.bloom) {
      this.hooks.setBloomMode(cur.bloom);
    }
    if (cur.dprCap1 !== prev.dprCap1) {
      this.hooks.setDprCap1(cur.dprCap1);
    }
    this.hooks.onStep(cur.bloom, cur.dprCap1);
  }
}
